"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _useBreakpoints = _interopRequireDefault(require("@ux/component-utilities/use-breakpoints"));
var _useEventListener = _interopRequireDefault(require("@ux/component-utilities/use-event-listener"));
var _useControllableState = _interopRequireDefault(require("@ux/component-utilities/use-controllable-state"));
var _labels = _interopRequireDefault(require("./labels"));
var _sliderThumbs = _interopRequireDefault(require("./slider-thumbs"));
var _valueLabels = _interopRequireDefault(require("./value-labels"));
var _rangeIndicator = _interopRequireDefault(require("./range-indicator"));
var _ticks = _interopRequireDefault(require("./ticks"));
var _utils = require("./utils");
var _classnames = _interopRequireDefault(require("classnames"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /* eslint-disable complexity */
const UX_SLIDER_ARIA_LABELS = {
  sliderThumb: 'Slider range handle'
};

/**
 * Gets slider starting value by validating the defaultValue prop.
 * @param {number} props.defaultValue default slider value
 * @param {number} props.min slider minimum value
 * @param {number} props.minRangeStep slider minimum amound of steps
 * @param {number} props.step handler step value
 * @param {boolean} props.range whether slider has multiple thumbs
 * @param {number} props.max slider max value
 * @returns {number[]} The starting value, rounded to nearest step if necessary
 * @private
 */
function getStartingValue(_ref) {
  let {
    defaultValue,
    min,
    minRangeStep,
    step,
    range,
    max
  } = _ref;
  let value;
  if (range && Array.isArray(defaultValue)) {
    defaultValue = defaultValue.map(val => (0, _utils.getNearestStepValue)(val, min, max, step));
    value = defaultValue;
  } else if (!range && typeof defaultValue === 'number') {
    defaultValue = (0, _utils.getNearestStepValue)(defaultValue, min, max, step);
    value = defaultValue;
  } else if (range) {
    value = [min, min + minRangeStep * step];
  } else {
    value = min;
  }
  return value;
}

/**
 * Returns width and position of filled slider area based on value.
 *
 * @param {number[]} value current thumb value
 * @param {boolean} props.range determines if slider is a range or not
 * @param {boolean} props.rtl determines if reverse
 * @param {number} props.min - min slider value
 * @param {number} props.max - max slider value
 * @param {number} clientWidth current slider width
 * @returns {object} Style of filled slider area
 * @private
 */
function getSliderFillStyle(value, _ref2, clientWidth) {
  let {
    range,
    rtl,
    min,
    max
  } = _ref2;
  const dir = rtl ? 'right' : 'left';
  if (range) {
    const sliderFillPos = (0, _utils.getPositionFromValue)(value[0], min, max, clientWidth);
    const sliderFillWidth = (0, _utils.getPositionFromValue)(value[1], min, max, clientWidth) - sliderFillPos;
    return {
      width: sliderFillWidth,
      [dir]: sliderFillPos
    };
  }
  const sliderFillWidth = (0, _utils.getPositionFromValue)(Array.isArray(value) ? value[0] : value, min, max, clientWidth);
  return {
    width: sliderFillWidth,
    [dir]: '0'
  };
}

/**
 * UXCore based Slider.
 * A UX component to select a value from a range using predetermined quanta.'
 * @param {React.PropsWithoutRef} props react component props
 * @returns {React.FunctionComponent} react slider compoennt
 */
// eslint-disable-next-line max-statements
function Slider(_ref3) {
  let {
    allowClickUpdate = true,
    labels = [],
    tooltipLabels = [],
    tooltipLabelFormat = value => value,
    hideTooltip = false,
    id = 'ux-slider',
    rangeIndicators = [null, null],
    continuous = false,
    step = 1,
    showFill = true,
    onChange: onControllableChange = Function.prototype,
    defaultValue = 0,
    minRangeStep = 1,
    min = 0,
    max = 10,
    range = false,
    valueLabelAutoHide = 750,
    forceIndicators = false,
    rtl = typeof document !== 'undefined' && document.documentElement && document.documentElement.dir === 'rtl',
    ariaLabels: _ariaLabels,
    className,
    value: controllableProp
  } = _ref3;
  const slider = (0, _react.useRef)(null);
  const _immediateId = (0, _react.useRef)(null);
  const {
    breakpoint
  } = (0, _useBreakpoints.default)('mobile', 'desktop');
  const [sliderWidth, setSliderWidth] = (0, _react.useState)({
    sliderRenderWidth: 0,
    sliderClientWidth: 0
  });
  const ariaLabels = _objectSpread(_objectSpread({}, UX_SLIDER_ARIA_LABELS), _ariaLabels);
  const [value, setValue] = (0, _useControllableState.default)({
    prop: controllableProp,
    defaultProp: getStartingValue({
      defaultValue,
      min,
      minRangeStep,
      step,
      range,
      max
    }),
    onChange: onControllableChange
  });
  const [isContinuous] = (0, _react.useState)(() => {
    let cont = continuous;
    if (tooltipLabels.length > 0 && cont) {
      // eslint-disable-next-line no-console
      console.warn('Warning: Custom tooltip labels should only be used with discrete sliders');
    }
    if (range && cont) {
      // eslint-disable-next-line no-console
      console.warn('Warning: Range sliders must be discrete');
      cont = false;
    }
    return cont;
  });
  const shouldRenderLabels = labels.length > 0 && tooltipLabels.length === 0 && !isContinuous;
  const [valueLabels, setValueLabels] = (0, _react.useState)(() => range ? [false, false] : [false]);

  /**
   * Sets sliderWidth state on resize
   * @private
   */
  const resize = (0, _react.useCallback)(() => {
    // Debouces to prevent multiple calls on re-render
    if (_immediateId.current) return;
    _immediateId.current = window.setTimeout(() => {
      _immediateId.current = null;
      if (!slider.current) {
        return;
      }
      const sliderRect = slider.current.getBoundingClientRect();
      let sliderRenderWidth = sliderRect.width || sliderRect.right - sliderRect.left;
      let sliderClientWidth = slider.current.clientWidth;
      // subtract rounded endcaps to get actual slider width
      sliderRenderWidth -= _utils.SLIDER_BAR_HEIGHT;
      sliderClientWidth -= _utils.SLIDER_BAR_HEIGHT;
      setSliderWidth({
        sliderRenderWidth,
        sliderClientWidth
      });
    }, 1);
  }, []);

  /**
   * Once the Slider has been rendered, listen to the
   * appropriate changes in Window size so it can be responsive.
   *
   * @returns {void}
   * @api private
   */

  (0, _react.useEffect)(() => {
    const timeoutId = window.setTimeout(resize, 1);
    return () => {
      _immediateId.current && window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handles breakpoints and clean up
   */
  (0, _useEventListener.default)('resize', resize);
  return /*#__PURE__*/_react.default.createElement("div", {
    "data-version": "2400",
    className: (0, _classnames.default)('ux-slider', className),
    id: id
  }, /*#__PURE__*/_react.default.createElement(_rangeIndicator.default, {
    range: range,
    allowClickUpdate: allowClickUpdate,
    min: min,
    value: value,
    setValue: setValue,
    id: id,
    max: max,
    step: step,
    breakpoint: breakpoint,
    continuous: isContinuous,
    forceIndicators: forceIndicators,
    rangeIndicators: rangeIndicators,
    side: "left"
  }), /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-slider-main"
  }, /*#__PURE__*/_react.default.createElement(_labels.default, {
    rtl: rtl,
    labels: labels,
    renderWidth: sliderWidth.sliderRenderWidth,
    shouldRenderLabels: shouldRenderLabels
  }), !hideTooltip && /*#__PURE__*/_react.default.createElement(_valueLabels.default, {
    breakpoint: breakpoint,
    shouldRenderLabels: shouldRenderLabels,
    range: range,
    value: value,
    valueLabels: valueLabels,
    clientWidth: sliderWidth.sliderRenderWidth,
    rtl: rtl,
    step: step,
    min: min,
    max: max,
    tooltipLabels: tooltipLabels,
    format: tooltipLabelFormat
  }), /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-slider-bar",
    ref: slider
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-slider-area"
  }, showFill && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("div", {
    className: "ux-slider-fill",
    style: getSliderFillStyle(value, {
      range,
      rtl,
      min,
      max
    }, sliderWidth.sliderRenderWidth)
  })), /*#__PURE__*/_react.default.createElement(_ticks.default, {
    rtl: rtl,
    continuous: isContinuous,
    min: min,
    max: max,
    step: step,
    range: range,
    value: value,
    setValue: setValue,
    allowClickUpdate: allowClickUpdate,
    clientWidth: sliderWidth.sliderRenderWidth
  })), /*#__PURE__*/_react.default.createElement(_sliderThumbs.default, {
    sliderRef: slider,
    autoHide: valueLabelAutoHide,
    renderWidth: sliderWidth.sliderRenderWidth,
    setValueLabels: setValueLabels,
    setValue: setValue,
    currentValues: value,
    shouldRenderLabels: shouldRenderLabels,
    range: range,
    step: step,
    minRangeStep: minRangeStep,
    allowClickUpdate: allowClickUpdate,
    dir: rtl ? 'right' : 'left',
    thumbs: range ? value : [value],
    id: id,
    max: max,
    min: min,
    rtl: rtl,
    ariaLabels: ariaLabels.sliderThumb
  }))), /*#__PURE__*/_react.default.createElement(_rangeIndicator.default, {
    range: range,
    allowClickUpdate: allowClickUpdate,
    min: min,
    value: value,
    setValue: setValue,
    id: id,
    max: max,
    step: step,
    breakpoint: breakpoint,
    continuous: isContinuous,
    rangeIndicators: rangeIndicators,
    forceIndicators: forceIndicators,
    side: "right"
  }));
}

/**
 * Additional properties when working with Slider
 *
 * @enum {object} expected types of properties.
 * @api public
 */
Slider.propTypes = {
  /** Allow change value by clicking in the ticks */
  allowClickUpdate: _propTypes.default.bool,
  /** The list of values to select (e.g: "100%", "small", 1, etc)*/
  labels: _propTypes.default.array,
  /** List of custom values to display in tooltips */
  tooltipLabels: _propTypes.default.array,
  /** Transformation function that formats tooltip labels */
  tooltipLabelFormat: _propTypes.default.func,
  /** Determines whether tooltip labels should be hidden */
  hideTooltip: _propTypes.default.bool,
  /** Unique identifier for the slider */
  id: _propTypes.default.string,
  /** Displayed to the left and right of the slider (should have two elements)*/
  rangeIndicators: _propTypes.default.array,
  /** When set slider will be continuous (as opposed to discrete/incremental) (default = false)*/
  continuous: _propTypes.default.bool,
  /** The amount that the handler skips over (ex: 5, 10, 15) (default = 1)*/
  step: _propTypes.default.number,
  /** Determines whether slider should show fill or not */
  showFill: _propTypes.default.bool,
  /** Callback for every slider value change*/
  onChange: _propTypes.default.func,
  /** The default value/index position for the selector.
  If slider is in range mode default value should be an array (default = min) */
  defaultValue: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.array]),
  /** The minimum number of required steps between min and max range sliders.
  Overlap can occur if minRangeStep is 0, otherwise, the steps between
  the start and end values need to be >= the minRangeStep. (default = 1)*/
  minRangeStep: _propTypes.default.number,
  /** The minimum amount of the slider (default = 0)*/
  min: _propTypes.default.number,
  /** The maximum amount of the slider (default = 10) */
  max: _propTypes.default.number,
  /** If set allows the component to set 2 thumbs (default = false) */
  range: _propTypes.default.bool,
  /** How long value labels remain visible after hovering */
  valueLabelAutoHide: _propTypes.default.number,
  /** Determines whether slider should always show range indicators on mobile */
  forceIndicators: _propTypes.default.bool,
  /** Determines whether slider should render in RTL mode */
  rtl: _propTypes.default.bool,
  /** Screenreader labels for sub-elements */
  ariaLabels: _propTypes.default.shape({
    /** Screen reader label for slider handle */
    sliderThumb: _propTypes.default.string
  }),
  /** Classes applied to the shell for positioning purposes */
  className: _propTypes.default.string,
  /** Allows for input to be controlled */
  value: _propTypes.default.number
};
var _default = exports.default = Slider;